import React from 'react';

const JsonEditorForm = ({ jsonData, path, onDataChange, onFocusField }) => {
  if (typeof jsonData !== 'object' || jsonData === null) {
    return <p>Invalid data format for editor.</p>;
  }

  const styles = {
    section: {
      marginBottom: '25px',
      padding: '20px',
      border: '1px solid #e0e0e0',
      borderRadius: '8px',
      backgroundColor: '#fdfdfd',
    },
    sectionTitle: {
      fontSize: '1.3em',
      fontWeight: '600',
      color: '#333',
      marginBottom: '15px',
      paddingBottom: '10px',
      borderBottom: '1px solid #eee',
      textTransform: 'capitalize',
    },
    fieldGroup: {
      marginBottom: '15px',
    },
    label: {
      display: 'block',
      fontSize: '0.95em',
      color: '#555',
      marginBottom: '6px',
      fontWeight: '500',
      textTransform: 'capitalize',
    },
    input: {
      width: '100%',
      padding: '10px',
      border: '1px solid #ccc',
      borderRadius: '4px',
      fontSize: '1em',
      boxSizing: 'border-box',
    },
    textarea: {
      width: '100%',
      padding: '10px',
      border: '1px solid #ccc',
      borderRadius: '4px',
      fontSize: '1em',
      minHeight: '100px',
      boxSizing: 'border-box',
      fontFamily: 'inherit',
    },
    infoText: {
      fontSize: '0.9em',
      color: '#777',
      fontStyle: 'italic',
    }
  };

  // Helper to generate a more readable label from a key
  const formatLabel = (key) => {
    return key.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase());
  };

  // Helper to check if a string value looks like a common image/video URL
  const isMediaUrl = (value) => {
    if (typeof value !== 'string') return false;
    return /\.(jpeg|jpg|gif|png|svg|mp4|webm|ogg)$/i.test(value);
  };

  const handleFocus = (currentPath) => {
    if (onFocusField) {
      onFocusField(currentPath);
    }
  };

  return (
    <div>
      {Object.entries(jsonData).map(([key, value]) => {
        const currentPath = path ? `${path}.${key}` : key;
        const label = formatLabel(key);

        if (isMediaUrl(value)) {
          return (
            <div key={currentPath} style={styles.fieldGroup}>
              <label style={styles.label} htmlFor={currentPath}>{label}</label>
              <p style={styles.infoText}>Media URL (not editable): <a href={value} target="_blank" rel="noopener noreferrer">{value}</a></p>
            </div>
          );
        }

        if (typeof value === 'string') {
          const isTextarea = value.length > 80 || value.includes('\n');
          return (
            <div key={currentPath} style={styles.fieldGroup}>
              <label style={styles.label} htmlFor={currentPath}>{label}</label>
              {isTextarea ? (
                <textarea
                  id={currentPath}
                  style={styles.textarea}
                  value={value}
                  onChange={(e) => onDataChange(currentPath, e.target.value)}
                  onFocus={() => handleFocus(currentPath)}
                  placeholder={`Enter ${label}...`}
                />
              ) : (
                <input
                  type="text"
                  id={currentPath}
                  style={styles.input}
                  value={value}
                  onChange={(e) => onDataChange(currentPath, e.target.value)}
                  onFocus={() => handleFocus(currentPath)}
                  placeholder={`Enter ${label}...`}
                />
              )}
            </div>
          );
        } else if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
          return (
            <div key={currentPath} style={styles.section}>
              <h3 style={styles.sectionTitle}>{label}</h3>
              <JsonEditorForm
                jsonData={value}
                path={currentPath}
                onDataChange={onDataChange}
                onFocusField={onFocusField}
              />
            </div>
          );
        } else if (Array.isArray(value)) {
            const stringArrayValues = value.filter(item => typeof item === 'string');
            if (stringArrayValues.length > 0 && stringArrayValues.length === value.length) {
                 return (
                    <div key={currentPath} style={styles.fieldGroup}>
                        <label style={styles.label}>{label} (List of texts)</label>
                        {stringArrayValues.map((item, index) => {
                            const itemPath = `${currentPath}.${index}`;
                            return (
                                <input
                                    key={itemPath}
                                    type="text"
                                    style={{...styles.input, marginBottom: '5px'}}
                                    value={item}
                                    onChange={(e) => onDataChange(itemPath, e.target.value)}
                                    onFocus={() => handleFocus(itemPath)}
                                />
                            );
                        })}
                    </div>
                 );
            }
          return (
            <div key={currentPath} style={styles.fieldGroup}>
              <label style={styles.label}>{label} (Array)</label>
              <pre style={{fontSize: '0.8em', backgroundColor: '#eee', padding: '10px', borderRadius: '4px'}}>
                {JSON.stringify(value, null, 2)}
              </pre>
              <p style={styles.infoText}>Direct editing for complex arrays or mixed-type arrays is not supported.</p>
            </div>
          );
        }
        else if (typeof value === 'number' || typeof value === 'boolean') {
             return (
                <div key={currentPath} style={styles.fieldGroup}>
                    <label style={styles.label} htmlFor={currentPath}>{label}</label>
                    <input
                        type={typeof value === 'number' ? 'number' : 'text'}
                        id={currentPath}
                        style={styles.input}
                        value={String(value)}
                        onChange={(e) => {
                            const newValue = typeof value === 'number' ? parseFloat(e.target.value) : e.target.value;
                            onDataChange(currentPath, newValue);
                        }}
                        onFocus={() => handleFocus(currentPath)}
                    />
                </div>
             );
        }

        return null;
      })}
    </div>
  );
};

export default JsonEditorForm; 